import {getAppraisalMethod, stripIndicationDetailFields} from "@reva/shared/indication-data";
import {getIndicationName} from "@reva/shared/indication-table";
import type {MarketAndLendingValue} from "@reva/shared/number-format";
import {SEGMENT} from "@reva/shared/reva-spec";
import type {RevaIndicationEndpoint} from "@reva/shared/spec-conversion";

import type {ValueIndicationResponse} from "../api/payload-types/value-indication";
import type {IndicationParameterDetails} from "../redux/valueIndicationSlice";

/**
 * Combine the indication details with the general indication response (of Value Indication),
 * and the request data. Null when the method did not run — the response then carries no detail
 * block for it.
 */
export function combineIndicationData(
    indicationData: ValueIndicationResponse,
    requestData: Record<string, unknown>,
    indicationDetails: Record<string, unknown> | undefined,
): IndicationParameterDetails | null {
    if (!indicationDetails) return null;
    const indicationOutputDataPure = stripIndicationDetailFields(indicationData);

    return {
        input: {...requestData},
        output: {...indicationOutputDataPure, ...indicationDetails},
        combined: {
            ...indicationOutputDataPure,
            ...requestData,
            ...indicationDetails,
        },
    };
}

const APPRAISAL_DETAILS_FIELD = {
    COMPARATIVE_VALUE: "comparative_value_details",
    INCOME_VALUE: "income_value_details",
    REAL_ASSET_VALUE: "real_asset_value_details",
} as const;

/** *_value_details-Block des Verfahrens, dem der Marktwert laut `indication_appraisal` folgt. */
export function getAppraisalValueDetails(
    indicationData: ValueIndicationResponse | null | undefined,
): Record<string, unknown> | undefined {
    const method = getAppraisalMethod(indicationData?.indication_appraisal);
    return method && indicationData?.[APPRAISAL_DETAILS_FIELD[method]];
}

/**
 * Combine the general indication response (of Value Indication) with the request data.
 */
export function combineValueData(
    indicationData: ValueIndicationResponse,
    requestData: Record<string, unknown>,
): IndicationParameterDetails {
    const indicationOutputDataPure = stripIndicationDetailFields(indicationData);

    return {
        input: {...requestData},
        output: indicationOutputDataPure,
        combined: {...indicationOutputDataPure, ...requestData},
    };
}

/** Value/lending-value figures extracted from the combined indication data for display. */
export interface IndicationValueAndLendingValueData extends MarketAndLendingValue {
    indication?: number | string | null;
    indication_hint?: number | string | null;
    indication_hint_de?: number | string | null;
    indicationNameDe?: string | null;
    indicationNameEn?: string | null;
    gross_profit?: number | string | null;
    gross_profit_lending?: number | string | null;
    income_value?: number | string | null;
    income_value_lending?: number | string | null;
    showWarning?: boolean;
}

/** Loosely-typed read view of an IndicationParameterDetails.combined record (dynamic AVM JSON). */
type CombinedIndicationValues = Record<string, number | string | null | undefined>;

/**
 * Extract the value and lending value from the combined indication data for the given endpoint.
 * Falls back to the VALUE method when no endpoint is given; for the WHG_M segment the market rent
 * is used instead of the value.
 */
export function getValueAndLendingValue(
    combinedIndicationData: Record<RevaIndicationEndpoint, IndicationParameterDetails | null> | null,
    outputParams: Record<string, unknown> | null | undefined,
    segment: string | null | undefined,
    endpoint: RevaIndicationEndpoint | null | undefined,
): IndicationValueAndLendingValueData | null {
    const indicationDataRaw =
        endpoint != null
            ? combinedIndicationData?.[endpoint as RevaIndicationEndpoint]?.combined
            : combinedIndicationData?.VALUE?.combined;
    const incomeValueDetails = combinedIndicationData?.INCOME_VALUE?.combined as CombinedIndicationValues | undefined;

    if (typeof indicationDataRaw !== "object" || !indicationDataRaw) {
        return null;
    }
    const indicationData = indicationDataRaw as CombinedIndicationValues;
    const useRent = segment === SEGMENT.WHG_M;

    switch (endpoint) {
        case "REAL_ASSET_VALUE":
            return {
                value: useRent ? indicationData.market_rent : indicationData.real_asset_value,
                value_per_sqm: useRent ? indicationData.market_rent_per_sqm : indicationData.real_asset_value_per_area,
                lending_value: indicationData.real_asset_value_lending,
                indication: indicationData.indication,
                lending_value_per_sqm: indicationData.real_asset_value_lending_per_area,
            };
        case "COMPARATIVE_VALUE":
            return {
                value: useRent ? indicationData.market_rent : indicationData.comparative_value,
                value_per_sqm: useRent ? indicationData.market_rent_per_sqm : indicationData.comparative_value_per_sqm,
                lending_value: indicationData.comparative_value_lending,
                indication: indicationData.indication,
                lending_value_per_sqm: indicationData.comparative_value_lending_per_sqm,
            };
        case "INCOME_VALUE":
            return {
                value: useRent ? indicationData.market_rent : indicationData.income_value,
                value_per_sqm: useRent ? indicationData.market_rent_per_sqm : indicationData.income_value_per_area,
                lending_value: indicationData.income_value_lending,
                lending_value_per_sqm: indicationData.income_value_lending_per_area,
                gross_profit: incomeValueDetails?.gross_profit,
                gross_profit_lending: incomeValueDetails?.gross_profit_lending,
                income_value: incomeValueDetails?.income_value,
                income_value_lending: incomeValueDetails?.income_value_lending,
                indication: indicationData.indication,
            };
        default: {
            const {indicationNameDe, indicationNameEn} = getIndicationName(indicationData, outputParams);

            return {
                value: useRent ? indicationData.market_rent : indicationData.value,
                value_per_sqm: useRent ? indicationData.market_rent_per_sqm : indicationData.value_per_sqm,
                lending_value: indicationData.value_lending,
                lending_value_per_sqm: indicationData.value_lending_per_sqm,
                gross_profit: incomeValueDetails?.gross_profit,
                gross_profit_lending: incomeValueDetails?.gross_profit_lending,
                income_value: incomeValueDetails?.income_value,
                income_value_lending: incomeValueDetails?.income_value_lending,
                indicationNameDe,
                indicationNameEn,
                showWarning:
                    indicationData.indication === 0 ||
                    Boolean(indicationData.indication_hint || indicationData.indication_hint_de),
                indication: indicationData.indication,
                indication_hint: indicationData.indication_hint,
                indication_hint_de: indicationData.indication_hint_de,
            };
        }
    }
}
